import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full' | 'auto';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements AfterViewInit, OnChanges {
  @ViewChild('dialogElement', { static: true }) dialogElement!: ElementRef<HTMLDialogElement>;

  @Input() visible: boolean = false;
  @Input() title: string = '';
  @Input() size: ModalSize = 'md';
  @Input() width: string = ''; // Custom width (overrides size)
  @Input() minWidth: string = '';
  @Input() maxHeight: string = '';
  @Input() showHeader: boolean = true;
  @Input() showFooter: boolean = false;
  @Input() showCloseButton: boolean = true;
  @Input() closeOnBackdrop: boolean = true;
  @Input() enableScroll: boolean = true; // Default: enable scrolling to prevent button clipping
  @Input() customClass: string = '';
  @Input() bodyClass: string = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onClose = new EventEmitter<void>();

  getDialogClasses(): string {
    const classes = [
      'rounded-lg',
      'shadow-xl',
      'backdrop:bg-black',
      'backdrop:bg-opacity-50',
      'p-0',
      'border-0',
    ];

    if (this.enableScroll) {
      classes.push('modal-scrollable');
    } else {
      classes.push('modal-auto-height');
    }

    if (this.customClass) {
      classes.push(this.customClass);
    }

    return classes.join(' ');
  }

  getContainerClasses(): string {
    const classes: string[] = [];

    // Size-based width classes
    if (!this.width) {
      const sizeMap: Record<ModalSize, string> = {
        xs: 'w-96', // 384px (increased from 320px)
        sm: 'w-[500px]', // 500px (increased from 384px)
        md: 'w-[600px]', // 600px (increased from 500px)
        lg: 'w-[750px]', // 750px (increased from 600px)
        xl: 'w-[900px]', // 900px (increased from 800px)
        '2xl': 'w-[1000px]', // 1000px (increased from 900px)
        '3xl': 'w-[1200px]', // 1200px (increased from 1000px)
        full: 'w-screen max-w-[90vw]',
        auto: 'w-auto',
      };
      classes.push(sizeMap[this.size]);
    }

    return classes.join(' ');
  }

  getBodyClasses(): string {
    const classes: string[] = [];

    if (this.enableScroll) {
      classes.push('body-scrollable');
    } else {
      classes.push('body-auto');
    }

    if (this.bodyClass) {
      classes.push(this.bodyClass);
    }

    return classes.join(' ');
  }

  ngAfterViewInit() {
    if (this.visible) {
      this.open();
    }
    this.dialogElement.nativeElement.addEventListener('close', () => {
      if (this.visible) {
        this.visible = false;
        this.visibleChange.emit(false);
        this.onClose.emit();
      }
    });
  }

  ngOnChanges() {
    if (this.dialogElement) {
      if (this.visible && !this.dialogElement.nativeElement.open) {
        this.open();
      } else if (!this.visible && this.dialogElement.nativeElement.open) {
        this.dialogElement.nativeElement.close();
      }
    }
  }

  open() {
    this.dialogElement.nativeElement.showModal();
  }

  close() {
    const dialog = this.dialogElement.nativeElement;
    if (dialog.open) {
      dialog.close();
    }
    this.visible = false;
    this.visibleChange.emit(false);
    this.onClose.emit();
  }

  onBackdropClick(event: MouseEvent) {
    // Handle both dialog backdrop click and custom backdrop click
    const target = event.target as HTMLElement;
    if (this.closeOnBackdrop && 
        (target === this.dialogElement.nativeElement || target.classList.contains('modal-custom-backdrop'))) {
      this.close();
    }
  }
}
